import { FC, useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { isEmpty } from 'radash';
import { favoriteStateStore } from '@modules/favorite/adapter/outbound/stores/favourite.store';
import { recipeStore } from '@modules/recipes/adapter/outbound/stores/recipeDetail.store';
import { recipeRelatedProductStore } from '@modules/recipes/adapter/outbound/stores/recipeRelatedProduct.store';
import { recipeRelatedRecipeStore } from '@modules/recipes/adapter/outbound/stores/recipeRelatedRecipe.store';
import { recommendTipsTricksStore } from '@modules/tipsAndTricks/adapter/outbound/stores/recommendTipsTricks.store';
import { userStore } from '@modules/user/adapter/outbound/stores/user.store';
import { Cooking, Detail, RecipeCover, RecipeIngredient, RelatedRecipe, RelatedTips } from '@components/recipe/detail';
import RecipeCoverSkeleton from '@components/skeleton/recipe/detail/RecipeCover';
import { ModuleType } from '@shared/types/module.type';
import useLockedBody from '@shared/utils/hooks/useLockedBody.utils';
import { analytics } from '@shared/utils/segment.utils';
const MainProduct = dynamic(() => import('@components/recipe/detail/mainProduct/MainProduct'));
const LoginModal = dynamic(() => import('@components/modal/login/LoginModal'));
const RecipeDetailProvider: FC = () => {
  const router = useRouter();
  const [_locked, setLocked] = useLockedBody(false, 'root');
  const [isLike, setIsLike] = useState<boolean>(false);
  const [isModalLoginOpen, setIsModalLoginOpen] = useState<boolean>(false);
  const {
    recipeDetail,
    fetchRecipeDetail
  } = recipeStore(state => state);
  const {
    relatedProductList,
    fetchRelatedProductList
  } = recipeRelatedProductStore(state => state);
  const {
    relatedRecipeList,
    fetchRelatedRecipe
  } = recipeRelatedRecipeStore(state => state);
  const {
    recommendTipsTricks,
    fetchRecommendTipsTricks
  } = recommendTipsTricksStore(state => state);
  const {
    isFavoriteItem,
    updateFavoriteItemById,
    resetData
  } = favoriteStateStore(state => state);
  const {
    userDetail
  } = userStore(state => state);
  const {
    creator
  } = recipeDetail;
  useEffect(() => {
    resetData();
  }, []);
  useEffect(() => {
    if (router.query.slug) {
      const slug = router.query.slug as string;
      fetchRecipeDetail(slug);
      fetchRelatedProductList(slug);
    }
    if (recommendTipsTricks.length === 0) {
      fetchRecommendTipsTricks();
    }
  }, [router.query.slug, userDetail]);
  useEffect(() => {
    if (recipeDetail.id) {
      fetchRelatedRecipe(recipeDetail.id);
      setLocked(false);
      analytics.track('Recipe Detail Viewed', {
        event: 'Recipe Detail Viewed',
        recipe_name: recipeDetail.title,
        ingredient: relatedProductList?.map(item => item.name)
      });
    } else {
      setLocked(true);
    }
  }, [recipeDetail]);
  useEffect(() => {
    setIsLike(isFavoriteItem);
  }, [isFavoriteItem]);
  const onUpdateLikeState = () => {
    if (userDetail) {
      updateFavoriteItemById(recipeDetail.id, ModuleType.Recipe);
      setIsLike(prev => !prev);
    } else {
      setIsModalLoginOpen(true);
    }
  };
  return recipeDetail.id ? <>
            <RecipeCover coverImg={recipeDetail.coverImg} coverVdo={recipeDetail.coverVdo} pageName={recipeDetail.title} />
            <Detail creatorImg={creator.imageUrl} creatorName={creator.name} title={recipeDetail.title} description={recipeDetail.description} cookingTime={recipeDetail.cookingTime} people={recipeDetail.people} isLike={isLike} onUpdateLikeState={onUpdateLikeState} />
            <RecipeIngredient data={recipeDetail.ingredients} />
            <Cooking content={recipeDetail.content} />
            {!isEmpty(relatedProductList) && <MainProduct data={relatedProductList} />}
            <RelatedRecipe data={relatedRecipeList} />
            <RelatedTips data={recommendTipsTricks} />
            <LoginModal isOpen={isModalLoginOpen} onClose={() => setIsModalLoginOpen(false)} />
        </> : <RecipeCoverSkeleton />;
};
export default RecipeDetailProvider;